import { GlobalSearchComponent } from './shared/utils/global-search/global-search';
import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import outputs from '../amplify_outputs.json';
import { Amplify } from 'aws-amplify';
import { NotificationBellComponent } from './shared/utils/notification-bell/notification-bell';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { NavbarComponent } from './shared/utils/navbar/navbar';
import { UserService } from './shared/services/user.service';
import { ClientService } from './shared/services/client.service';
import { User } from './shared/services/user';
import { ProjectService } from './shared/services/project.service';
import { TaskService } from './shared/services/task.service';
import { TenantService } from './shared/services/tenant.service';
import { NotificationService } from './shared/services/notification.service';
import { TauriService } from './shared/services/tauri.service';
import { Hub } from 'aws-amplify/utils';
import { ToastComponent } from './shared/utils/toast/toast';
import { PosthogService } from './shared/services/posthog.service';
import { getCurrentUser, signOut } from '@aws-amplify/auth';
import { UpdaterService } from './shared/services/updater';
import { isTauri } from '@tauri-apps/api/core';
import { getVersion } from '@tauri-apps/api/app';
import { info } from '@tauri-apps/plugin-log';
import { filter } from 'rxjs/operators';
import {Menu} from './shared/utils/menu/menu';
import { AddTaskModalComponent } from './shared/utils/add-task-modal/add-task-modal';
import { CalendarPopoverComponent } from './shared/utils/calendar-popover/calendar-popover';
import { SidebarProjectsComponent } from './shared/utils/sidebar-projects/sidebar-projects';
import { RecentProjectsService } from './shared/services/recent-projects.service';

@Component({
  selector: 'app-root',
  imports: [AmplifyAuthenticatorModule,
    CommonModule,
    FormsModule,
    RouterOutlet,
    NotificationBellComponent,
    NavbarComponent,
    ToastComponent, Menu,
    AddTaskModalComponent,
    CalendarPopoverComponent,
    SidebarProjectsComponent, GlobalSearchComponent],
  templateUrl: './app.html'
})

export class App {
  protected readonly title = signal('sel-studio-angular-app');
  private userService = inject(UserService);
  private clientService = inject(ClientService);
  private user = inject(User); // Add User service for auth data
  private projectService = inject(ProjectService);
  private taskService = inject(TaskService);
  private tenantService = inject(TenantService);
  private notificationService = inject(NotificationService);
  private tauriService = inject(TauriService);
  private router = inject(Router);
  private recentProjects = inject(RecentProjectsService);
  posthogService = inject(PosthogService);
  private updater = inject(UpdaterService);

  showNavbar = true;

  // Global top bar
  readonly today = new Date();
  newMenuOpen = false;
  showNewTaskModal = false;

  /** Whether the Tasks feature is enabled (gates the "New task" shortcut). */
  get hasTasksAccess(): boolean {
    return this.posthogService.featureFlagTasks();
  }


  /** "New" shortcut → create a project (opens the create modal on the projects page). */
  createProject() {
    this.newMenuOpen = false;
    this.router.navigate(['/projects'], { queryParams: { new: 1 } });
  }

  /** "New" shortcut → create a task in-place (opens the modal without navigating). */
  createTask() {
    this.newMenuOpen = false;
    this.showNewTaskModal = true;
  }

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.showNavbar = !event.urlAfterRedirects.startsWith('/login');
      // Record opened project (route /projects/:id) for the sidebar recents.
      const m = event.urlAfterRedirects.match(/^\/projects\/([^/?#]+)/);
      if (m && m[1] && m[1] !== 'new') this.recentProjects.recordOpen(m[1]);
    });
  }

  async ngOnInit() {
    // Configure Amplify first, before anything else
    Amplify.configure(outputs);

    // Set initial navbar visibility
    this.showNavbar = !this.router.url.startsWith('/login');

    // Small delay to ensure Amplify is fully configured
    await new Promise(resolve => setTimeout(resolve, 100));

    // Check if user is already authenticated and initialize services
    try {
      console.log('[App] Checking for authenticated user...');
      const authUser = await getCurrentUser();
      console.log('[App] User authenticated:', authUser.username);
      if (authUser) {
        // User is already authenticated, initialize all services including User auth service
        console.log('[App] Initializing services...');
        await Promise.all([
          this.user.init(), // Initialize User service first for auth data
          this.userService.init(),
          this.projectService.init(),
          this.taskService.init(),
          this.tenantService.init(),
          this.notificationService.init()
        ]);
        console.log('[App] All services initialized');
        this.posthogService.identifyUser(authUser);
      }
    } catch (error) {
      // User not authenticated, that's fine
      console.log('[App] No authenticated user found:', error);
    }

    // Not awaited: the check ends in a native modal, and awaiting it held back
    // everything below — including the auth Hub listener, which stayed
    // unregistered (so a sign-in or sign-out was missed) for as long as the
    // update dialog sat on screen unanswered.
    if (isTauri()) {
      void (async () => {
        await info(`[app] version: ${await getVersion()}`);
        await info('[app] calling updater.checkAndUpdate()');
        await this.updater.checkAndUpdate();
      })();
    }

    Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signedOut') {
        if (isTauri()) {
          await info('[auth] signed out');
        }
        this.user.resetState(); // Reset User auth service
        this.userService.resetState();
        this.projectService.resetState();
        this.tauriService.resetState();
        this.tenantService.resetState();
        this.clientService.resetState();
        this.posthogService.resetUser(); // ✅ Arrêter l'enregistrement PostHog
        await this.router.navigate(['/login']);
      }

      if (payload.event === 'signedIn') {
        if (isTauri()) {
          await info('[auth] signed in');
        }
        // Initialize all services after sign in, including User auth service
        await Promise.all([
          this.user.init(), // Initialize User service first for auth data
          this.userService.init(),
          this.projectService.init(),
          this.taskService.init(),
          this.tenantService.init(),
          this.notificationService.init()
        ]);
        const fullUser = await getCurrentUser();
        this.posthogService.identifyUser(fullUser);
        await this.router.navigate(['/projects']);
      }
    });
  }

  async handleSignOut() {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }
}
